import { Box, Heading, Flex, Badge } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { PillWithDetail, PillInfoAffix } from './PillWithDetail'

// Animation variant for section reveal
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.15,
      ease: [0.25, 0.46, 0.45, 0.94] as [number, number, number, number],
    },
  },
}

type TraitCategory = 'style' | 'cuisine' | 'flavor' | 'aversion'

/**
 * Get gradient color for trait badges based on category.
 */
export function getTraitColor(category: TraitCategory): string {
  switch (category) {
    case 'cuisine':
      return 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)' // Hot pink
    case 'flavor':
      return 'linear-gradient(135deg, #059669 0%, #10b981 100%)' // Rich emerald
    case 'style':
      return 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)' // Rich violet
    case 'aversion':
      return 'linear-gradient(135deg, #ef4444 0%, #f87171 100%)' // True red
    default:
      return 'purple.500'
  }
}

interface TraitSectionProps {
  title: string
  items: string[] | undefined
  category: TraitCategory
}

/**
 * Reusable component for displaying trait badge sections.
 * Flattened DOM structure using Flex instead of Wrap/WrapItem.
 */
export function TraitSection({ title, items, category }: TraitSectionProps) {
  if (!items?.length) return null

  const bgGradient = getTraitColor(category)

  return (
    <Box as={motion.div} variants={itemVariants}>
      <Heading size="sm" mb={3} color="var(--card-text)">
        {title}
      </Heading>
      <Flex wrap="wrap" gap={2} maxW="100%" overflow="hidden">
        {items.map((item, idx) => (
          <PillWithDetail key={idx} rawLabel={item}>
            {(label, hasDetail) => (
              <Badge
                bgGradient={bgGradient}
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="md"
                fontWeight="medium"
                maxW="100%"
                whiteSpace="normal"
                wordBreak="break-word"
                boxShadow="0 2px 4px var(--color-overlay-20)"
                textShadow="0 1px 2px var(--color-overlay-70)"
              >
                {label}
                {hasDetail && <PillInfoAffix />}
              </Badge>
            )}
          </PillWithDetail>
        ))}
      </Flex>
    </Box>
  )
}
